import React, { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { ReactComponent as FileIcon } from 'src/assets/tailwind/file.svg';
import { ReactComponent as FolderIcon } from 'src/assets/tailwind/folder.svg';
import { ReactComponent as UploadIcon } from 'src/assets/tailwind/upload-files.svg';
import { Button } from 'src/components/ui/button';
import { Icon } from 'src/components/ui/icon';
import { Spinner } from 'src/components/ui/spinner';
import { Typography } from 'src/components/ui/typography';
import { UPLOAD_TYPE } from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { cn } from 'src/utils/utils';

export function DropzoneArea({
  onDrop,
  isUploadFileAllowed = true
}: {
  onDrop: (files: File[], type: UPLOAD_TYPE) => void;
  isUploadFileAllowed?: boolean;
}) {
  const { t } = useTranslation();
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [isPreparingFolder, setIsPreparingFolder] = useState(false);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (files) => {
      const cleanedFiles = files.filter((file) => !file.name.startsWith('.'));
      onDrop(cleanedFiles, UPLOAD_TYPE.FILE);
    },
    noClick: true,
    noKeyboard: true,
    multiple: true
  });

  const handleFolderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPreparingFolder(true);
    if (folderInputRef.current) {
      folderInputRef.current.click();
    }
  };

  useEffect(() => {
    const input = folderInputRef.current;
    if (!input) return undefined;
    const handleCancel = () => setIsPreparingFolder(false);
    input.addEventListener('cancel', handleCancel);
    return () => input.removeEventListener('cancel', handleCancel);
  }, []);

  const handleFolderSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPreparingFolder(false);
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      const cleanedFiles = filesArray.filter(
        (file) => !file.name.startsWith('.')
      );
      onDrop(cleanedFiles, UPLOAD_TYPE.FOLDER);
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
  };

  return (
    <div
      {...getRootProps()}
      className={cn(
        `
        tw:h-[384px]
        tw:border-1 tw:border-dashed tw:border-border-primary tw:rounded-bw-2xl-3xl
        tw:py-3xl tw:px-5xl tw:text-center
        tw:flex tw:flex-col tw:items-center
        tw:w-full
        tw:transition-all tw:duration-200`,
        isDragActive
          ? 'tw:border-border-primary-hover tw:bg-primary-hover tw:scale-[1.02]'
          : 'tw:border-border-primary'
      )}
    >
      <input {...getInputProps()} />

      <input
        type="file"
        ref={folderInputRef}
        onChange={handleFolderSelection}
        style={{ display: 'none' }}
        webkitdirectory="true"
        directory="true"
        {...({} as any)}
      />

      {isPreparingFolder ? (
        <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full">
          <Spinner text={t(T.preparingFolderFiles)} />
        </div>
      ) : (
        <>
          <div className="tw:p-xl tw:self-center tw:mt-3xl tw:mb-m tw:bg-brand-light tw:max-w-max tw:rounded-full ">
            <Icon icon={UploadIcon} size={32} colorClass="tw:text-icon-brand" />
          </div>

          <Typography
            variant="b2"
            colorClass="tw:text-text-secondary"
            className="tw:mb-3"
          >
            {t(T.uploadSupportedFormatsDescription)}
          </Typography>

          <Typography variant="b2" colorClass="tw:text-text-secondary">
            {t(T.supportedDocFormats)}
            <br />
            {t(T.supportedMediaFormats)}
            <br />
            {t(T.supportedSpreadsheetFormats)}
          </Typography>

          <div className="tw:flex tw:justify-center tw:gap-l tw:mt-3xl">
            <Button
              hierarchy="secondary"
              text={t(T.uploadDocs)}
              leftIcon={FileIcon}
              onClick={open}
              size="small"
              disabled={!isUploadFileAllowed}
            />
            <Button
              hierarchy="secondary"
              text={t(T.uploadFolder)}
              leftIcon={FolderIcon}
              onClick={handleFolderClick}
              size="small"
            />
          </div>
        </>
      )}
    </div>
  );
}
